import {
  Button,
  Checkbox,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@design-system/shadcn";
import { cn } from "@design-system/utils";
import { DndContext, closestCenter } from "@dnd-kit/core";
import {
  restrictToParentElement,
  restrictToVerticalAxis,
} from "@dnd-kit/modifiers";
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { type Column } from "@tanstack/react-table";
import { ChevronDown, Columns3, GripVertical, RotateCcw } from "lucide-react";
import React, { useId, type ReactNode } from "react";

import { useDataTableContext } from "../../hooks/use-data-table-context";

import { useColumnReorder } from "./use-column-reorder";

interface ColumnSelectProps {
  label: string;
  tooltip?: string;
  resetLabel?: string;
  // The defaults pluralize and phrase in English; an app with i18n passes its own
  // (a Lingui `plural`, say) so the count and the handle's name are localized.
  renderHiddenCount?: (count: number) => ReactNode;
  renderReorderLabel?: (columnLabel: string) => string;
  className?: string;
}

function defaultHiddenCount(count: number): string {
  return `${count} hidden column${count === 1 ? "" : "s"}`;
}

function defaultReorderLabel(columnLabel: string): string {
  return `Reorder ${columnLabel}`;
}

function getColumnLabel(column: Column<unknown, unknown>): string {
  const { header, meta } = column.columnDef;
  if (meta?.label) {
    return meta.label;
  }
  if (typeof header === "string") {
    return header;
  }
  return column.id;
}

interface ColumnSelectRowProps {
  column: Column<unknown, unknown>;
  renderReorderLabel: (columnLabel: string) => string;
}

function ColumnSelectRow({ column, renderReorderLabel }: ColumnSelectRowProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: column.id });
  const checkboxId = useId();
  const label = getColumnLabel(column);

  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Translate.toString(transform), transition }}
      className={cn(
        "hover:bg-shape-neutral-ghost-background-hover flex items-center gap-3 rounded-lg px-2 py-1.5",
        isDragging && "bg-shape-neutral-ghost-background-hover relative z-10"
      )}
    >
      <Checkbox
        id={checkboxId}
        checked={column.getIsVisible()}
        onCheckedChange={() => column.toggleVisibility()}
      />
      <label
        htmlFor={checkboxId}
        className="text-foreground-neutral-default flex-1 cursor-pointer text-sm"
      >
        {label}
      </label>
      <button
        type="button"
        aria-label={renderReorderLabel(label)}
        className="text-shape-neutral-ghost-foreground-muted hover:text-shape-neutral-ghost-foreground-default focus-visible:ring-ring-default cursor-grab rounded outline-none focus-visible:ring-2"
        {...attributes}
        {...listeners}
      >
        <GripVertical className="size-4" aria-hidden />
      </button>
    </div>
  );
}

/**
 * Per-column show/hide plus drag-reorder inside ONE surface, beside
 * `FilterBar.Menu` on a filter row.
 *
 * A `Popover` rather than a `DropdownMenu`: a Radix menu item dismisses its menu
 * on select and owns the arrow keys, so neither a visibility toggle nor a
 * keyboard drag would survive inside one.
 *
 * Only hideable columns are listed — a fixed column can be neither hidden nor
 * moved, so offering it a row would be offering a control that does nothing.
 */
function ColumnSelect({
  label,
  tooltip,
  resetLabel = "Reset all",
  renderHiddenCount = defaultHiddenCount,
  renderReorderLabel = defaultReorderLabel,
  className,
}: ColumnSelectProps) {
  const { table } = useDataTableContext<unknown, unknown>();
  const { sensors, orderedColumns, reorderableIds, handleDragEnd } =
    useColumnReorder(table);

  const hideableColumns = orderedColumns.filter((column) =>
    column.getCanHide()
  );
  const hiddenCount = hideableColumns.filter(
    (column) => !column.getIsVisible()
  ).length;

  // Both writes reach the consumer through its own change handlers, so whatever
  // it does with the state — including storing it — sees the reset like any edit.
  function resetAll() {
    table.resetColumnVisibility();
    table.resetColumnOrder();
  }

  const trigger = (
    <PopoverTrigger asChild>
      <Button
        type="button"
        aria-label={label}
        className={cn(
          "aria-expanded:bg-shape-neutral-outlined-background-pressed",
          className
        )}
      >
        <Columns3 aria-hidden />
        <ChevronDown aria-hidden />
      </Button>
    </PopoverTrigger>
  );

  return (
    <Popover>
      {tooltip ? (
        <Tooltip>
          <TooltipTrigger asChild>{trigger}</TooltipTrigger>
          <TooltipContent>{tooltip}</TooltipContent>
        </Tooltip>
      ) : (
        trigger
      )}
      <PopoverContent align="end" className="w-64 p-2" aria-label={label}>
        <div className="mb-2 flex items-center justify-between gap-2 px-2">
          <span className="text-foreground-neutral-muted text-xs">
            {renderHiddenCount(hiddenCount)}
          </span>
          <Button variant="ghost" size="smaller" onClick={resetAll}>
            <RotateCcw aria-hidden />
            {resetLabel}
          </Button>
        </div>
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          modifiers={[restrictToVerticalAxis, restrictToParentElement]}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={reorderableIds}
            strategy={verticalListSortingStrategy}
          >
            <div>
              {hideableColumns.map((column) => (
                <ColumnSelectRow
                  key={column.id}
                  column={column}
                  renderReorderLabel={renderReorderLabel}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      </PopoverContent>
    </Popover>
  );
}

export { ColumnSelect };
export type { ColumnSelectProps };
